'use client';
import { FC, useEffect } from 'react';
import { Content, ImageField, asText, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import HighlightedText from '@/components/HighlightedText/HighlightedText';
import clsx from 'clsx';

// The `lottie-player` web component is registered globally in src/types/lottie.d.ts

const HighlightedHeadline: FC<{ text: string }> = ({ text }) => (
  <h1 className="h1-hero text-center font-inter font-medium text-white">
    <HighlightedText text={text} />
  </h1>
);

export type HeadlineWithIllustrationProps =
  SliceComponentProps<Content.HeadlineWithIllustrationSlice>;

const LightWithIllustration: FC<{
  slice: HeadlineWithIllustrationProps['slice'];
}> = ({ slice }) => {
  // Register the lottie-player web component on the client only
  useEffect(() => {
    import('@lottiefiles/lottie-player');
  }, []);

  const primary = slice.primary as unknown as {
    eyebrow?: string;
    headline?: import('@prismicio/client').RichTextField;
    description?: import('@prismicio/client').RichTextField;
    emphasis?: string;
    illustration?: import('@prismicio/client').LinkToMediaField;
  };
  const { eyebrow, headline, description, emphasis, illustration } = primary;
  const lottieUrl = isFilled.linkToMedia(illustration)
    ? illustration.url
    : null;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white px-4 py-16 font-inter md:px-8 md:py-24"
    >
      <div className="mx-auto flex w-full max-w-[1140px] flex-col items-center gap-[20px] text-center">
        {eyebrow && (
          <span className="inline-block rounded-full border border-[#4B0FEA] px-[18px] py-[8px] text-[12px] font-normal uppercase leading-[1.2] tracking-[0.12em] text-[#041118] md:text-[13px]">
            {eyebrow}
          </span>
        )}

        {isFilled.richText(headline) && (
          <PrismicRichText
            field={headline}
            components={{
              heading2: ({ children }) => (
                <h2 className="max-w-[1040px] text-[32px] font-bold leading-[1.15] text-[#041118] md:text-[44px]">
                  {children}
                </h2>
              ),
            }}
          />
        )}

        {isFilled.richText(description) && (
          <div className="max-w-[820px]">
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-[15px] font-light leading-[1.55] text-[#041118]/70 md:text-[16px]">
                    {children}
                  </p>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
                hyperlink: ({ children, node }) => (
                  <a
                    href={node.data.url ?? '#'}
                    className="text-[#4B0FEA] underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                ),
              }}
            />
          </div>
        )}

        {emphasis && (
          <p className="max-w-[820px] text-[15px] font-medium leading-[1.5] text-[#041118] md:text-[16px]">
            {emphasis}
          </p>
        )}

        {lottieUrl && (
          <div className="mt-[24px] w-full max-w-[640px] md:mt-[32px]">
            <lottie-player
              src={lottieUrl}
              autoplay
              loop
              background="transparent"
              speed="1"
              style={{
                width: '100%',
                height: 'auto',
                aspectRatio: '1 / 1',
              }}
            />
          </div>
        )}
      </div>
    </section>
  );
};

const HeadlineWithIllustration: FC<HeadlineWithIllustrationProps> = ({
  slice,
}) => {
  if ((slice.variation as string) === 'lightWithIllustration') {
    return <LightWithIllustration slice={slice} />;
  }
  const { headline, description, illustration } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="headline-section"
    >
      <div className="container mx-auto max-w-6xl text-center">
        {/* Headline with Green Highlights */}
        <div className="mb-10">
          {headline && <HighlightedHeadline text={asText(headline) || ''} />}

          <PrismicRichText
            field={description}
            components={{
              paragraph: ({ children }: { children: React.ReactNode }) => (
                <p className="text-gray-300 text-lg md:text-xl leading-relaxed max-w-4xl mx-auto">
                  {children}
                </p>
              ),
            }}
          />
        </div>

        {/* Illustration/Diagram Section */}
        <div className="px-2">
          {illustration ? (
            <div className="w-full px-4 sm:px-6 md:px-0">
              <PrismicNextImage
                field={illustration as ImageField}
                className="mx-auto block w-full max-w-[490px] h-auto sm:h-[280px] md:h-[340px] lg:h-[385px]"
                priority
                fallbackAlt=""
              />
            </div>
          ) : (
            /* Velx Workflow Diagram */
            <div className="diagram-wrapper">
              {/* Central Velx Logo */}
              <div className={clsx('central-node', 'z-10 font-inter')}>
                <span>Velx</span>
              </div>

              {/* Data Source (Left) */}
              <div className="absolute top-1/2 left-8 -translate-y-1/2 transform">
                <div className="bg-[#05F273] rounded-lg p-4 w-20 h-24 flex flex-col items-center justify-center">
                  <div className="text-[#0E1010] font-semibold text-xs mb-1">
                    Data
                  </div>
                  <div className="w-full space-y-1">
                    <div className="h-1 bg-[#0E1010] rounded"></div>
                    <div className="h-1 bg-[#0E1010] rounded"></div>
                    <div className="h-1 bg-[#0E1010] rounded"></div>
                    <div className="h-1 bg-[#0E1010] rounded"></div>
                  </div>
                  <div className="flex space-x-1 mt-2">
                    <div className="w-1 h-1 bg-[#0E1010] rounded-full"></div>
                    <div className="w-1 h-1 bg-[#0E1010] rounded-full"></div>
                    <div className="w-1 h-1 bg-[#0E1010] rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* AI-Based Intelligence (Bottom Right) */}
              <div className="absolute bottom-8 right-8">
                <div className={clsx('ai-module', 'p-4')}>
                  <div className="text-gray-800 font-semibold text-xs mb-2">
                    AI-Based Intelligence
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-sm"></div>
                    </div>
                    <div className="flex-1">
                      <div className="h-1 bg-yellow-400 rounded mb-1"></div>
                      <div className="h-1 bg-gray-300 rounded"></div>
                    </div>
                    <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <div className="w-2 h-1 bg-white rounded"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Connected Nodes */}
              <div className="absolute top-16 right-32">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">CI</span>
                </div>
              </div>

              <div className="absolute top-32 right-16">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">DB</span>
                </div>
              </div>

              <div className="absolute bottom-32 left-32">
                <div className="w-6 h-6 bg-green-500 rounded-full"></div>
              </div>

              {/* Dotted Connection Lines */}
              <svg className="diagram-lines">
                {/* Data to Velx */}
                <line
                  x1="25%"
                  y1="50%"
                  x2="45%"
                  y2="50%"
                  stroke="#05F273"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />

                {/* Velx to AI Intelligence */}
                <line
                  x1="55%"
                  y1="60%"
                  x2="75%"
                  y2="80%"
                  stroke="#05F273"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />

                {/* Velx to top nodes */}
                <line
                  x1="55%"
                  y1="45%"
                  x2="75%"
                  y2="25%"
                  stroke="#05F273"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />

                {/* Velx to bottom left */}
                <line
                  x1="45%"
                  y1="55%"
                  x2="35%"
                  y2="75%"
                  stroke="#05F273"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Styled-jsx for component-specific styles */}
      <style jsx>{`
        .headline-section {
          padding-top: 2.5rem;
          padding-bottom: 2.5rem;
          background-color: #0e1010;
        }

        .diagram-wrapper {
          position: relative;
          max-width: 56rem;
          margin-left: auto;
          margin-right: auto;
        }

        .central-node {
          position: absolute;
          top: 50%;
          left: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.25rem;
          width: 6rem;
          height: 6rem;
          border-radius: 9999px;
          transform: translate(-50%, -50%);
          background: #05f273;
          color: #0e1010;
        }

        .ai-module {
          position: absolute;
          border-radius: 0.5rem;
          box-shadow:
            0 10px 15px -3px rgb(0 0 0 / 0.1),
            0 4px 6px -4px rgb(0 0 0 / 0.1);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          width: 8rem;
          height: 5rem;
          background: #ffffff;
          color: #1f2937;
        }

        .diagram-node {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.75rem;
          color: white;
          border-radius: 9999px;
          width: 2rem;
          height: 2rem;
          background: rgba(5, 242, 115, 0.8);
        }

        .diagram-lines {
          pointer-events: none;
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          z-index: 1;
        }
      `}</style>
    </section>
  );
};

export default HeadlineWithIllustration;
